import { Card } from '@trafiflow/shared-ui'
import {
  UserCheck,
  Search,
  Star,
  TrendingUp,
  Users,
  BarChart3,
  Instagram,
  Youtube,
  Filter,
  ChevronRight,
} from 'lucide-react'

export function CreatorEvaluator() {
  const topCreators = [
    {
      id: 1,
      name: 'Alex Rivera',
      handle: '@alexrivera',
      followers: '2.4M',
      engagement: '4.2%',
      score: 92,
      niche: 'Lifestyle',
      platform: 'instagram',
    },
    {
      id: 2,
      name: 'Sarah Chen',
      handle: '@sarahchen',
      followers: '1.8M',
      engagement: '5.1%',
      score: 89,
      niche: 'Tech',
      platform: 'youtube',
    },
    {
      id: 3,
      name: 'Marcus Johnson',
      handle: '@marcusj',
      followers: '3.2M',
      engagement: '3.8%',
      score: 87,
      niche: 'Fitness',
      platform: 'instagram',
    },
    {
      id: 4,
      name: 'Emily Watson',
      handle: '@emilyw',
      followers: '950K',
      engagement: '6.2%',
      score: 85,
      niche: 'Fashion',
      platform: 'instagram',
    },
  ]

  const metrics = [
    { label: 'Creators Analyzed', value: '12,450', icon: Users, color: 'blue' },
    { label: 'Avg. Engagement Rate', value: '4.8%', icon: TrendingUp, color: 'green' },
    { label: 'Top Performer Score', value: '98', icon: Star, color: 'yellow' },
    { label: 'Categories Tracked', value: '24', icon: BarChart3, color: 'purple' },
  ]

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-tf-text">Creator Evaluator</h1>
        <p className="text-tf-text-muted mt-1">
          Analyze and score creators based on performance, engagement, and brand fit
        </p>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-tf-text-muted" />
          <input
            type="text"
            placeholder="Search creators by name, handle, or niche..."
            className="w-full pl-10 pr-4 py-2.5 border border-tf-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <button className="flex items-center gap-2 px-4 py-2.5 border border-tf-border rounded-lg hover:bg-tf-bg-muted transition-colors">
          <Filter className="w-4 h-4" />
          <span>Filters</span>
        </button>
      </div>

      {/* Metrics Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {metrics.map((metric) => {
          const Icon = metric.icon
          const colorClasses: Record<string, string> = {
            blue: 'bg-blue-100 text-blue-600',
            green: 'bg-green-100 text-green-600',
            yellow: 'bg-yellow-100 text-yellow-600',
            purple: 'bg-primary/15 text-primary',
          }
          return (
            <Card key={metric.label} className="p-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClasses[metric.color]}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-tf-text">{metric.value}</p>
                  <p className="text-sm text-tf-text-muted">{metric.label}</p>
                </div>
              </div>
            </Card>
          )
        })}
      </div>

      {/* Evaluate Creator CTA */}
      <Card className="p-6 mb-8 bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
            <UserCheck className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-tf-text">Evaluate a Creator</h3>
            <p className="text-sm text-tf-text-muted">
              Enter a creator's profile URL to get an instant AI-powered evaluation
            </p>
          </div>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2">
            <span>Evaluate Now</span>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </Card>

      {/* Top Creators */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-tf-text">Top Rated Creators</h2>
          <button className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1">
            View All <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="space-y-3">
          {topCreators.map((creator) => (
            <Card
              key={creator.id}
              className="p-4 hover:shadow-md transition-shadow cursor-pointer"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center text-white font-bold">
                  {creator.name.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-tf-text">{creator.name}</h3>
                    {creator.platform === 'instagram' ? (
                      <Instagram className="w-4 h-4 text-pink-500" />
                    ) : (
                      <Youtube className="w-4 h-4 text-red-500" />
                    )}
                  </div>
                  <p className="text-sm text-tf-text-muted">{creator.handle}</p>
                </div>
                <div className="hidden sm:flex items-center gap-6 text-sm">
                  <div className="text-center">
                    <p className="font-semibold text-tf-text">{creator.followers}</p>
                    <p className="text-tf-text-muted">Followers</p>
                  </div>
                  <div className="text-center">
                    <p className="font-semibold text-tf-text">{creator.engagement}</p>
                    <p className="text-tf-text-muted">Engagement</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-tf-text-muted mb-1">Niche</p>
                    <span className="px-2 py-1 bg-tf-bg-hover rounded text-xs font-medium">
                      {creator.niche}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <span className="text-lg font-bold text-green-600">{creator.score}</span>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  )
}

export default CreatorEvaluator
